import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import type {
  CategoryId,
  EditActionDto,
  TransactionDto,
  TransactionId,
  TransactionListDto,
  TransactionUpdateDto,
} from "../../api/types";
import {
  coalesceActions,
  fieldChangeToActions,
  type FieldChange,
} from "../../logic/edit-actions";
import { usePreviewTransactionUpdate } from "../../api/queries";
import { useRegistry } from "../../context/RegistryContext";
import { effectiveRow } from "./rowView";

/**
 * The pending edits for one row.
 *
 * `overlay` is what the user just did, applied locally so the cell updates the
 * instant the edit is committed. `preview` is the server's authoritative
 * rendering of `actions` (rules expanded, conditionals resolved) and replaces
 * the overlay's view of the row when it lands. A preview is only accepted if
 * it answers for the *current* `actions` — a slow response for an older edit
 * is dropped rather than overwriting a newer one.
 */
export type RowEdit = {
  actions: EditActionDto[];
  overlay: Partial<TransactionListDto>;
  preview: TransactionDto | null;
};

/**
 * Edit state for the transaction list, held outside React so a change to one
 * row notifies only that row's subscribers. The list container re-renders for
 * its own reasons (paging, dialogs); typing, committing and preview responses
 * never touch it — that is the entire point of this store.
 */
export const createEditSession = (
  /// Resolves to the server's preview of the row with the actions applied.
  /// Rejections are swallowed: the optimistic overlay already shows the edit,
  /// and saving does not depend on previews.
  requestPreview: (
    id: TransactionId,
    actions: EditActionDto[],
  ) => Promise<TransactionDto>,
  /// Name → id, for diffing the current category names against a pick.
  resolveCategoryId: (name: string) => CategoryId | undefined,
) => {
  // Swappable after construction — see `setEnvironment`.
  let env = { requestPreview, resolveCategoryId };
  const rows = new Map<TransactionId, RowEdit>();
  const rowListeners = new Map<TransactionId, Set<() => void>>();
  const sessionListeners = new Set<() => void>();

  const notify = (id: TransactionId) => {
    rowListeners.get(id)?.forEach((cb) => cb());
    sessionListeners.forEach((cb) => cb());
  };

  const setRow = (id: TransactionId, next: RowEdit) => {
    rows.set(id, next);
    notify(id);
  };

  const append = (
    id: TransactionId,
    newActions: EditActionDto[],
    overlay: Partial<TransactionListDto>,
  ) => {
    if (newActions.length === 0) return;
    const row = rows.get(id);
    const actions = coalesceActions(row?.actions ?? [], newActions);
    setRow(id, {
      actions,
      overlay: { ...row?.overlay, ...overlay },
      preview: row?.preview ?? null,
    });
    env.requestPreview(id, actions).then(
      (details) => {
        const current = rows.get(id);
        // Only an answer to the actions as they stand now may land; `actions`
        // identity changes on every append, so this also orders responses.
        if (current?.actions !== actions) return;
        setRow(id, { ...current, preview: details });
      },
      () => {},
    );
  };

  return {
    subscribeRow: (id: TransactionId, cb: () => void) => {
      let set = rowListeners.get(id);
      if (!set) rowListeners.set(id, (set = new Set()));
      set.add(cb);
      return () => {
        set.delete(cb);
        if (set.size === 0) rowListeners.delete(id);
      };
    },
    subscribe: (cb: () => void) => {
      sessionListeners.add(cb);
      return () => sessionListeners.delete(cb);
    },
    getRow: (id: TransactionId) => rows.get(id),
    getEditedCount: () => rows.size,

    /// Commit a field edit: `overlay` is the display-side value shown
    /// immediately, `change` becomes wire actions diffed against the row as
    /// the pending edits have left it.
    commitFieldChange: (
      tx: TransactionListDto,
      change: FieldChange,
      overlay: Partial<TransactionListDto>,
    ) => {
      const effective = effectiveRow(tx, rows.get(tx.id));
      append(
        tx.id,
        fieldChangeToActions(change, effective, env.resolveCategoryId),
        overlay,
      );
    },

    /// Replace the wiring without touching edit state. `useEditSession` calls
    /// this after each render, so the session always resolves names against
    /// the current registry (an entity can be created inline mid-edit).
    setEnvironment: (next: typeof env) => {
      env = next;
    },

    /// Append pre-built actions (rule application, confirmed prompts). No
    /// local overlay is possible — the server resolves what they do — so the
    /// row shows as pending and the preview fills in the values.
    appendActions: (id: TransactionId, actions: EditActionDto[]) =>
      append(id, actions, {}),

    collectUpdates: (): TransactionUpdateDto[] =>
      [...rows.entries()]
        .filter(([, row]) => row.actions.length > 0)
        .map(([id, row]) => ({ id, actions: row.actions })),

    clearRow: (id: TransactionId) => {
      if (rows.delete(id)) notify(id);
    },
    clear: () => {
      const ids = [...rows.keys()];
      rows.clear();
      ids.forEach(notify);
    },
  };
};

export type EditSession = ReturnType<typeof createEditSession>;

/**
 * Create the session once — its identity, and that of every callback derived
 * from it, is stable for the life of the list — and keep its wiring current
 * via `setEnvironment` after each relevant change.
 */
export const useEditSession = (): EditSession => {
  const { mutateAsync: preview } = usePreviewTransactionUpdate();
  const { categoryMap } = useRegistry();

  const [session] = useState(() =>
    // Placeholder wiring; the effect below installs the real one before any
    // interaction can reach the session (effects run before the user can
    // click anything on the fresh render).
    createEditSession(
      () => Promise.reject(new Error("edit session not wired yet")),
      () => undefined,
    ),
  );
  useEffect(() => {
    session.setEnvironment({
      requestPreview: (id, actions) =>
        preview({ id, actions }).then((p) => p.details),
      resolveCategoryId: (name) => categoryMap[name]?.id,
    });
  }, [session, preview, categoryMap]);
  return session;
};

/// This row's pending edits, or undefined. Re-renders the caller only when
/// this row changes.
export const useRowEdit = (session: EditSession, id: TransactionId) =>
  useSyncExternalStore(
    useCallback(
      (cb: () => void) => session.subscribeRow(id, cb),
      [session, id],
    ),
    () => session.getRow(id),
  );

/// Number of rows with pending edits. Drives the Save/Cancel bar.
export const useEditedCount = (session: EditSession) =>
  useSyncExternalStore(
    useCallback((cb: () => void) => session.subscribe(cb), [session]),
    () => session.getEditedCount(),
  );
